// Train a programme together (#184): the invite link, and who's in.
import React, { useEffect, useState } from "react";
import { Check, Link2, Users } from "lucide-react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import { createProgrammeInvite } from "../../services/api";
import { buildTogetherUrl, shareOrCopyLink } from "../../utils/shareLinks";
import { useToast } from "../../contexts/ToastContext";

const TogetherSheet = ({ open, onOpenChange, programme, group }) => {
  const [code, setCode] = useState(group?.code || null);
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  useEffect(() => {
    if (group?.code) setCode(group.code);
  }, [group?.code]);

  const send = async () => {
    setBusy(true);
    try {
      const c = code || (await createProgrammeInvite(programme.id)).code;
      setCode(c);
      const result = await shareOrCopyLink({
        url: buildTogetherUrl(c),
        title: programme.title,
        text: `Train ${programme.title} with me on Khronos`,
      });
      if (result === "copied") showToast({ title: "Link copied" });
      if (result === "failed") showToast({ title: "Couldn't copy the link", variant: "destructive" });
    } catch (error) {
      showToast({ title: "Couldn't make the link", description: error.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const mates = group?.mates || [];
  return (
    <DetailSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Train together"
      description="Send mates a link. Each gets their own copy, on their own days."
      showDescription
      footer={
        <Button type="button" className="h-12 w-full text-base font-bold" onClick={send} disabled={busy}>
          <Link2 className="mr-2 h-4 w-4" />
          {code ? "Send the link again" : "Send a link"}
        </Button>
      }
    >
      <ul className="grid gap-2 text-sm leading-relaxed text-foreground">
        <li className="flex gap-2">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
          Whoever builds a session first shares it: the others see it, ready to start or adapt to their kit.
        </li>
        <li className="flex gap-2">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
          A mate&rsquo;s copy follows yours until they adapt or change it.
        </li>
        <li className="flex gap-2">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
          Ticks on each session show who&rsquo;s done it.
        </li>
      </ul>
      {mates.length > 1 && (
        <div className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border">
          {mates.map((m) => (
            <div key={m.user_id} className="flex items-center gap-2.5 px-3 py-2.5">
              {m.photo_url ? (
                <img src={m.photo_url} alt="" className="h-7 w-7 rounded-full" />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
              )}
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{m.me ? "You" : m.name}</span>
              <span className="text-xs text-muted-foreground">
                {m.owner ? "Started it" : `${m.done.length} done`}
              </span>
            </div>
          ))}
        </div>
      )}
    </DetailSheet>
  );
};

export default TogetherSheet;
